import React from 'react'
import type { ProjectGantViewProviderProps } from './Context'
import { ProjectGantViewProvider } from './Context'
import type { GanttTask } from '@ztechapps/webui.data-tools'
import { GanttChart } from '@ztechapps/webui.data-tools'

export const ProjectGanttView = (props: Omit<ProjectGantViewProviderProps, 'children'>) => {
  return (
    <ProjectGantViewProvider {...props}>
      <Layout />
    </ProjectGantViewProvider>
  )
}

const Layout = () => {
  const tasks: GanttTask[] = [
    { id: '1', name: 'Construction of a House', start: '2025-02-13', end: '2025-06-30' },

    // Foundation Phase
    { id: '1.1', parentId: '1', name: 'Foundation', start: '2025-02-13', end: '2025-02-28' },
    {
      id: '1.1.1',
      parentId: '1.1',
      name: 'Site Clearing',
      start: '2025-02-13',
      end: '2025-02-14',
      progress: 100,
      color: '#0d6efd',
    },
    {
      id: '1.1.2',
      parentId: '1.1',
      name: 'Excavation',
      start: '2025-02-15',
      end: '2025-02-18',
      progress: 100,
      color: '#20c997',
    },
    {
      id: '1.1.3',
      parentId: '1.1',
      name: 'Concrete Footings',
      start: '2025-02-19',
      end: '2025-02-22',
      progress: 90,
      color: '#ffc107',
    },
    {
      id: '1.1.4',
      parentId: '1.1',
      name: 'Foundation Walls',
      start: '2025-02-23',
      end: '2025-02-28',
      progress: 60,
      color: '#dc3545',
    },

    // Structure Phase
    { id: '1.2', parentId: '1', name: 'Structural Work', start: '2025-03-01', end: '2025-03-25' },
    {
      id: '1.2.1',
      parentId: '1.2',
      name: 'Framing',
      start: '2025-03-01',
      end: '2025-03-08',
      progress: 40,
      color: '#0dcaf0',
    },
    {
      id: '1.2.2',
      parentId: '1.2',
      name: 'Steel Erection',
      start: '2025-03-05',
      end: '2025-03-12',
      progress: 25,
      color: '#6610f2',
    },
    {
      id: '1.2.3',
      parentId: '1.2',
      name: 'Roof Framing',
      start: '2025-03-13',
      end: '2025-03-20',
      progress: 0,
      color: '#198754',
    },
    {
      id: '1.2.4',
      parentId: '1.2',
      name: 'Exterior Walls',
      start: '2025-03-15',
      end: '2025-03-25',
      progress: 0,
      color: '#fd7e14',
    },

    // Roofing
    { id: '1.3', parentId: '1', name: 'Roofing', start: '2025-03-26', end: '2025-04-02' },
    {
      id: '1.3.1',
      parentId: '1.3',
      name: 'Install Roof Decking',
      start: '2025-03-26',
      end: '2025-03-28',
      progress: 0,
      color: '#6f42c1',
    },
    {
      id: '1.3.2',
      parentId: '1.3',
      name: 'Install Shingles',
      start: '2025-03-29',
      end: '2025-04-02',
      progress: 0,
      color: '#20c997',
    },

    // Exterior Finishing
    { id: '1.4', parentId: '1', name: 'Exterior Finishing', start: '2025-04-03', end: '2025-04-15' },
    {
      id: '1.4.1',
      parentId: '1.4',
      name: 'Windows & Doors',
      start: '2025-04-03',
      end: '2025-04-06',
      progress: 0,
      color: '#0d6efd',
    },
    {
      id: '1.4.2',
      parentId: '1.4',
      name: 'Exterior Painting',
      start: '2025-04-07',
      end: '2025-04-12',
      progress: 0,
      color: '#ffc107',
    },
    {
      id: '1.4.3',
      parentId: '1.4',
      name: 'Exterior Fixtures',
      start: '2025-04-13',
      end: '2025-04-15',
      progress: 0,
      color: '#198754',
    },

    // Internal Works
    { id: '1.5', parentId: '1', name: 'Internal Works', start: '2025-04-16', end: '2025-05-20' },
    {
      id: '1.5.1',
      parentId: '1.5',
      name: 'Electrical',
      start: '2025-04-16',
      end: '2025-04-25',
      progress: 10,
      color: '#dc3545',
    },
    {
      id: '1.5.2',
      parentId: '1.5',
      name: 'Plumbing',
      start: '2025-04-18',
      end: '2025-04-28',
      progress: 0,
      color: '#20c997',
    },
    {
      id: '1.5.3',
      parentId: '1.5',
      name: 'HVAC',
      start: '2025-04-26',
      end: '2025-05-05',
      progress: 0,
      color: '#0dcaf0',
    },
    {
      id: '1.5.4',
      parentId: '1.5',
      name: 'Drywall',
      start: '2025-05-06',
      end: '2025-05-10',
      progress: 0,
      color: '#ffc107',
    },
    {
      id: '1.5.5',
      parentId: '1.5',
      name: 'Painting',
      start: '2025-05-11',
      end: '2025-05-15',
      progress: 0,
      color: '#6f42c1',
    },
    {
      id: '1.5.6',
      parentId: '1.5',
      name: 'Flooring',
      start: '2025-05-16',
      end: '2025-05-20',
      progress: 0,
      color: '#fd7e14',
    },

    // Landscaping
    { id: '1.6', parentId: '1', name: 'Landscaping', start: '2025-05-21', end: '2025-05-30' },
    {
      id: '1.6.1',
      parentId: '1.6',
      name: 'Driveway',
      start: '2025-05-21',
      end: '2025-05-23',
      progress: 0,
      color: '#20c997',
    },
    {
      id: '1.6.2',
      parentId: '1.6',
      name: 'Garden Setup',
      start: '2025-05-24',
      end: '2025-05-27',
      progress: 0,
      color: '#0d6efd',
    },
    {
      id: '1.6.3',
      parentId: '1.6',
      name: 'Fencing',
      start: '2025-05-28',
      end: '2025-05-30',
      progress: 0,
      color: '#6610f2',
    },

    // Inspections
    { id: '1.7', parentId: '1', name: 'Inspections & Handover', start: '2025-06-01', end: '2025-06-10' },
    {
      id: '1.7.1',
      parentId: '1.7',
      name: 'Safety Inspection',
      start: '2025-06-01',
      end: '2025-06-02',
      milestone: true,
    },
    { id: '1.7.2', parentId: '1.7', name: 'Final Inspection', start: '2025-06-09', end: '2025-06-09', milestone: true },
    { id: '1.7.3', parentId: '1.7', name: 'Handover', start: '2025-06-10', end: '2025-06-10', milestone: true },
  ]

  return (
    <div>
      <GanttChart tasks={tasks} view="month" />
    </div>
  )
}
